<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>IPsec回線一覧</span>
        <manual-button path="/ipsec-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="ipSec.search" v-model="ipSec.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn v-permission.config color="primary" @click="addIpSec"
              >追加</v-btn
            >
            <v-btn v-permission.config color="primary" @click="deleteIpSec"
              >削除</v-btn
            >
          </div>
        </v-row>
        <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="ipSec.sortBy"
          primary-key="xnumber"
          :fields="ipSec.fields"
          :items="items"
          :selectable="selectable"
          v-model="ipSec.selected"
          :filter-option="ipSec.filter_option"
          :filter="ipSec.filter"
          :hover="true"
          sticky-header="50vh"
          @click:row="showDetails"
        >
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import IpSecLineReference from "@/modals/ipSecLine/IpSecLineReference.vue";
import IpSecLineViewCD from "@/modals/ipSecLine/IpSecLineViewCD.vue";
import type {
  IpSecLineGetEntity,
  IpSecLineListEntity,
} from "@/apis/IpSecLineApi";
import IpSecLineEdit from "@/modals/ipSecLine/IpSecLineEdit.vue";
import type { IpSecGWEntity } from "@/apis/IpSecGwApi";
import { defineComponent } from "vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";

/** IPsec回線 + IPsecGW */
export type IpSecLineWithGW = IpSecLineListEntity & {
  // IPsec回線に紐作りIPsecGWは必ずサフィックスが存在する（サフィックスがないと追加できない）
  gw: Omit<IpSecGWEntity, "suffix"> & { suffix: string };
};

export default defineComponent({
  name: "IpSecLineList",
  data() {
    return {
      alertMsg: null as string | null,
      ipSec: {
        sortBy: {
          key: "xnumber",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "xnumber",
            title: "IPsec回線番号",
            sortable: true,
            width: "160px",
          },
          {
            key: "ipsecLineName",
            title: "IPsec回線名",
            sortable: true,
            width: "240px",
          },
          {
            key: "enumber",
            title: "IPsecGW回線番号",
            sortable: true,
            width: "180px",
          },
          {
            key: "suffix",
            title: "サフィックス",
            sortable: true,
            width: "200px",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: IpSecLineWithGW) => {
              return item.gw.suffix;
            },
          },
          {
            key: "gwLineSize",
            title: "同一IPsecGW回線数",
            align: "end",
            sortable: true,
            width: "240px",
            sortByFormatted: true,
            formatter: (value: string, key: null, item: IpSecLineWithGW) => {
              return `${item.gw.numberOfIpsec}/${item.gw.upperLimitIpsec}`;
            },
          },
          {
            key: "userAccount",
            title: "USER-FQDN",
            sortable: true,
            width: "240px",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: IpSecLineWithGW) => {
              return `${item.userAccount}@${item.gw.suffix}`;
            },
          },
          {
            key: "ifStatus",
            title: "IF状態",
            sortable: true,
            width: "180px",
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "cloudVn_ifStatus"),
          },
        ] as AppTableHeader<IpSecLineWithGW>[],
        selected: [] as IpSecLineWithGW[],
        search: {
          xnumber: { type: "text", label: "IPsec回線番号" },
          ipsecLineName: { type: "text", label: "IPsec回線名" },
          enumber: { type: "text", label: "IPsecGW回線番号" },
          suffix: { type: "text", label: "サフィックス" },
          userAccount: { type: "text", label: "USER-FQDN" },
          ifStatus: {
            type: "select",
            label: "IF状態",
            items: [
              { value: "NO_SHUTDOWN", text: "no shutdown" },
              { value: "SHUTDOWN", text: "shutdown" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          xnumber: "like",
          ipsecLineName: "like",
          enumber: "like",
          suffix: "like",
          userAccount: "like",
          ifStatus: "equal",
        } as FilterOption,
      },
      ipSecLine: {} as IpSecLineGetEntity,
      ipSecGws: [] as IpSecGWEntity[],
      isLoaded: false,
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
    /** IPsec回線 + IPsecGW */
    items(): IpSecLineWithGW[] {
      const gwByEnumber = this.ipSecGws.toMap((e) => e.enumber);
      return this.ipSecLine.ipsecList.map((e) => ({
        ...e,
        gw: gwByEnumber[e.enumber] as IpSecLineWithGW["gw"],
      }));
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.alertMsg = null;
      this.ipSecLine = await this.$api.ipSecLine.getAll();
      this.ipSecGws = (await this.$api.ipSecGw.getAll()).ipsecGatewayList;
    },
    /** IPsec回線追加 */
    async addIpSec() {
      this.alertMsg = null;
      await this.$modal.show(IpSecLineEdit);
      await this.load();
    },
    /** IPsec回線削除 */
    async deleteIpSec() {
      if (this.ipSec.selected.length > 0) {
        this.alertMsg = null;
        const note: string | null = await this.$modal.show(IpSecLineViewCD, {
          mode: "delete",
          lines: this.ipSec.selected,
        });
        await this.$api.ipSecLine.delete({
          ipsecList: this.ipSec.selected.map((x) => x.xnumber),
          acLineKeyId: this.ipSecLine.acLineKeyId,
          note: note,
        });
        await this.load();
      } else {
        this.alertMsg = this.$filter.message("no_select_data");
      }
    },

    /** IPsec回線情報画面表示 */
    async showDetails(item: IpSecLineWithGW) {
      this.alertMsg = null;
      await this.$modal.show(IpSecLineReference, {
        ipSecLine: item,
        keyId: this.ipSecLine.acLineKeyId,
      });
      await this.load();
    },
  },
});
</script>
